(() => {
  'use strict';

  const COUNTRY_NAMES = {
    DE: 'Deutschland', NL: 'Niederlande', BE: 'Belgien', CH: 'Schweiz', AT: 'Österreich',
    FR: 'Frankreich', PL: 'Polen', LU: 'Luxemburg', LT: 'Litauen', SK: 'Slowakei',
    SI: 'Slowenien', IS: 'Island', LV: 'Lettland', ES: 'Spanien', HR: 'Kroatien',
    DK: 'Dänemark', GB: 'Vereinigtes Königreich'
  };

  const toTextKey = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

  const toPostalKey = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]+/g, '');
  const MAP_MAX_STORES = 20;

  class StoreLocator {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-zsl-form]');
      this.queryInput = root.querySelector('[data-zsl-query]');
      this.countrySelect = root.querySelector('[data-zsl-country]');
      this.submitButton = root.querySelector('[data-zsl-submit]');
      this.geoButton = root.querySelector('[data-zsl-geolocate]');
      this.status = root.querySelector('[data-zsl-status]');
      this.flagshipTarget = root.querySelector('[data-zsl-flagship]');
      this.resultsTarget = root.querySelector('[data-zsl-results]');
      this.emptyTarget = root.querySelector('[data-zsl-empty]');
      this.countTarget = root.querySelector('[data-zsl-result-count]');
      this.moreButton = root.querySelector('[data-zsl-more]');
      this.viewToolbar = root.querySelector('[data-zsl-view-toolbar]');
      this.viewButtons = Array.from(root.querySelectorAll('[data-zsl-view]'));
      this.listPanel = root.querySelector('[data-zsl-list-panel]');
      this.mapPanel = root.querySelector('[data-zsl-map-panel]');
      this.mapTarget = root.querySelector('[data-zsl-map]');
      this.mapLoading = root.querySelector('[data-zsl-map-loading]');
      this.mapNote = root.querySelector('[data-zsl-map-note]');
      this.initialLimit = Math.max(5, Number(root.dataset.initialLimit) || 10);
      this.visibleLimit = this.initialLimit;
      this.sortedPartners = [];
      this.origin = null;
      this.stores = [];
      this.searchIndex = null;
      this.isReady = false;
      this.activeView = 'list';
      this.map = null;
      this.markerLayer = null;
      this.storeMarkers = new Map();
      this.init();
    }

    async init() {
      this.setDefaultCountry();
      this.bindEvents();
      this.setBusy(true, 'Storedaten werden geladen …');
      try {
        const [storesResponse, searchResponse] = await Promise.all([
          fetch(this.root.dataset.storesUrl, { credentials: 'same-origin' }),
          fetch(this.root.dataset.searchUrl, { credentials: 'same-origin' })
        ]);
        if (!storesResponse.ok || !searchResponse.ok) throw new Error('assets');
        const [stores, searchIndex] = await Promise.all([storesResponse.json(), searchResponse.json()]);
        if (!Array.isArray(stores) || !searchIndex || typeof searchIndex !== 'object') throw new Error('data');
        this.stores = stores.filter((store) => Number.isFinite(store.lat) && Number.isFinite(store.lng));
        this.searchIndex = searchIndex;
        this.flagship = this.stores.find((store) => store.flagship);
        this.partners = this.stores.filter((store) => !store.flagship);
        this.renderFlagship();
        this.isReady = true;
        this.setBusy(false);
        if (!this.applyUrlSearch()) {
          this.setStatus('Standort eingeben oder die Standortfreigabe nutzen, um Händler in deiner Nähe zu finden.');
        }
      } catch (error) {
        this.setBusy(false);
        this.setStatus('Der Storefinder konnte nicht geladen werden. Bitte versuche es später erneut.', 'error');
      }
    }

    setDefaultCountry() {
      const requested = String(this.root.dataset.defaultCountry || 'DE').toUpperCase();
      if (this.countrySelect.querySelector(`option[value="${requested}"]`)) this.countrySelect.value = requested;
    }

    bindEvents() {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.searchByInput();
      });
      this.geoButton.addEventListener('click', () => this.searchByDeviceLocation());
      this.moreButton.addEventListener('click', () => {
        this.visibleLimit += this.initialLimit;
        this.renderPartners();
      });
      this.viewButtons.forEach((button) => {
        button.addEventListener('click', () => this.setView(button.dataset.zslView));
      });
    }

    applyUrlSearch() {
      const params = new URLSearchParams(window.location.search);
      const country = String(params.get('country') || '').toUpperCase();
      if (country && this.countrySelect.querySelector(`option[value="${country}"]`)) {
        this.countrySelect.value = country;
      }
      const query = String(params.get('q') || '').trim();
      if (query) {
        this.queryInput.value = query;
        this.searchByInput();
        return true;
      }
      if (params.get('locate') === '1') {
        this.searchByDeviceLocation();
        return true;
      }
      return false;
    }

    setBusy(busy, message) {
      this.submitButton.disabled = busy;
      this.geoButton.disabled = busy;
      if (message) this.setStatus(message);
    }

    setStatus(message, state = '') {
      this.status.textContent = message;
      if (state) this.status.dataset.state = state;
      else delete this.status.dataset.state;
    }

    resolveInput(query, countryCode) {
      const postalKey = toPostalKey(query);
      const textKey = toTextKey(query);
      const countryPostal = this.searchIndex.postal?.[countryCode] || {};
      const countryCities = this.searchIndex.city?.[countryCode] || {};
      const countryPrefixes = this.searchIndex.prefix?.[countryCode] || {};

      if (/\d/.test(postalKey)) {
        if (countryPostal[postalKey]) return { coordinates: countryPostal[postalKey], method: 'postal', accuracy: 'exact' };
        const prefixLength = Number(this.searchIndex.prefixLength?.[countryCode] || 2);
        const prefix = postalKey.slice(0, prefixLength);
        if (countryPrefixes[prefix]) return { coordinates: countryPrefixes[prefix], method: 'postal_region', accuracy: 'region' };
      }
      if (countryCities[textKey]) return { coordinates: countryCities[textKey], method: 'city', accuracy: 'city' };
      const directStore = this.stores.find((store) => {
        if (String(store.countryCode || '').toUpperCase() !== countryCode) return false;
        return toPostalKey(store.postalCode) === postalKey
          || toTextKey(store.city) === textKey
          || toTextKey(store.name).includes(textKey);
      });
      if (directStore) return { coordinates: [directStore.lat, directStore.lng], method: 'store_fallback', accuracy: 'exact' };
      return null;
    }

    searchByInput() {
      if (!this.isReady) return;
      const query = this.queryInput.value.trim();
      const countryCode = this.countrySelect.value;
      if (!query) {
        this.setStatus('Bitte gib eine Stadt oder PLZ ein.', 'error');
        this.queryInput.focus();
        return;
      }
      const match = this.resolveInput(query, countryCode);
      if (!match) {
        this.setStatus('Der Ort konnte offline nicht eindeutig zugeordnet werden. Bitte prüfe das Land oder verwende eine PLZ.', 'error');
        return;
      }
      this.applyOrigin(match.coordinates[0], match.coordinates[1], {
        label: query,
        method: match.method,
        countryCode,
        approximate: match.accuracy === 'region'
      });
    }

    searchByDeviceLocation() {
      if (!this.isReady) return;
      if (!navigator.geolocation) {
        this.setStatus('Die Standortfunktion wird von diesem Browser nicht unterstützt.', 'error');
        return;
      }
      this.setBusy(true, 'Aktueller Standort wird ermittelt …');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.setBusy(false);
          this.applyOrigin(position.coords.latitude, position.coords.longitude, {
            label: 'deinem aktuellen Standort', method: 'device', countryCode: '', approximate: false
          });
        },
        (error) => {
          this.setBusy(false);
          const message = error.code === 1
            ? 'Die Standortfreigabe wurde abgelehnt. Du kannst alternativ Stadt oder PLZ eingeben.'
            : 'Der aktuelle Standort konnte nicht ermittelt werden. Bitte versuche es erneut oder nutze Stadt/PLZ.';
          this.setStatus(message, 'error');
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 300000 }
      );
    }

    applyOrigin(lat, lng, meta) {
      this.origin = { lat: Number(lat), lng: Number(lng), ...meta };
      this.visibleLimit = this.initialLimit;
      this.sortedPartners = this.partners
        .map((store) => ({ ...store, distance: this.distance(this.origin, store) }))
        .sort((a, b) => a.distance - b.distance || a.city.localeCompare(b.city, 'de') || a.name.localeCompare(b.name, 'de'));
      if (this.flagship) this.flagshipDistance = this.distance(this.origin, this.flagship);
      this.renderFlagship();
      this.renderPartners();
      this.viewToolbar.hidden = false;

      const country = meta.countryCode ? ` in ${COUNTRY_NAMES[meta.countryCode] || meta.countryCode}` : '';
      const approximation = meta.approximate ? ' Die PLZ wurde über ihre Region angenähert.' : '';
      this.setStatus(`Nächstgelegene Händler zu ${meta.label}${country} – nach Luftlinie sortiert.${approximation}`, 'success');
      this.pushAnalytics(meta.method, meta.countryCode);
    }

    distance(a, b) {
      const radius = 6371;
      const radians = (degrees) => degrees * Math.PI / 180;
      const dLat = radians(b.lat - a.lat);
      const dLng = radians(b.lng - a.lng);
      const lat1 = radians(a.lat);
      const lat2 = radians(b.lat);
      const value = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
      return 2 * radius * Math.asin(Math.sqrt(value));
    }

    renderFlagship() {
      this.flagshipTarget.replaceChildren();
      if (!this.flagship) return;
      this.flagshipTarget.append(this.createCard(this.flagship, { flagship: true, distance: this.flagshipDistance }));
    }

    renderPartners() {
      const visible = this.sortedPartners.slice(0, this.visibleLimit);
      this.resultsTarget.replaceChildren();
      visible.forEach((store, index) => {
        const item = document.createElement('li');
        item.append(this.createCard(store, { nearest: index === 0, distance: store.distance }));
        this.resultsTarget.append(item);
      });
      this.emptyTarget.hidden = visible.length > 0;
      this.countTarget.hidden = false;
      this.countTarget.textContent = `${this.sortedPartners.length} Händler gefunden`;
      this.moreButton.hidden = this.visibleLimit >= this.sortedPartners.length;
      const mapCount = Math.min(visible.length, MAP_MAX_STORES);
      this.mapNote.textContent = `Die Karte zeigt die ${mapCount} nächstgelegenen Händler und den Flagshipstore.`;
      if (this.activeView === 'map' && this.map) this.renderMap();
    }

    createCard(store, options = {}) {
      const card = document.createElement('article');
      card.className = `zsl__card ${options.flagship ? 'zsl__card--flagship' : 'zsl__card--partner'}`;
      card.dataset.storeId = store.id;

      const main = document.createElement('div');
      main.className = 'zsl__card-main';
      const badges = document.createElement('div');
      badges.className = 'zsl__badges';
      if (options.flagship) badges.append(this.createBadge('Flagshipstore'));
      if (options.nearest) badges.append(this.createBadge('Nächster Händler', 'zsl__badge--nearest'));
      if (badges.childElementCount) main.append(badges);

      const title = document.createElement('h3');
      title.className = 'zsl__card-title';
      title.textContent = store.name;
      main.append(title);

      const address = document.createElement('address');
      address.className = 'zsl__address';
      const streetLine = [store.street, store.number].filter(Boolean).join(' ');
      address.append(document.createTextNode(streetLine));
      address.append(document.createElement('br'));
      address.append(document.createTextNode(`${store.postalCode} ${store.city}`.trim()));
      address.append(document.createElement('br'));
      address.append(document.createTextNode(COUNTRY_NAMES[store.countryCode] || store.country));
      main.append(address);

      const actions = document.createElement('div');
      actions.className = 'zsl__card-actions';
      const route = document.createElement('a');
      route.className = 'zsl__route';
      route.textContent = 'Route planen';
      route.target = '_blank';
      route.rel = 'noopener';
      route.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${streetLine}, ${store.postalCode} ${store.city}, ${store.country}`)}`;
      actions.append(route);
      if (this.origin && Number.isFinite(store.lat) && Number.isFinite(store.lng)) {
        const mapButton = document.createElement('button');
        mapButton.className = 'zsl__show-map';
        mapButton.type = 'button';
        mapButton.textContent = 'Auf Karte';
        mapButton.addEventListener('click', () => this.setView('map', { focusStoreId: store.id }));
        actions.append(mapButton);
      }
      main.append(actions);
      card.append(main);

      if (Number.isFinite(options.distance)) {
        const distance = document.createElement('div');
        distance.className = 'zsl__distance';
        distance.textContent = this.formatDistance(options.distance);
        distance.setAttribute('aria-label', `${this.formatDistance(options.distance)} Entfernung`);
        card.append(distance);
      }
      return card;
    }

    async setView(view, options = {}) {
      if (!this.origin || !['list', 'map'].includes(view)) return;
      this.activeView = view;
      this.viewButtons.forEach((button) => {
        const active = button.dataset.zslView === view;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.listPanel.hidden = view !== 'list';
      this.mapPanel.hidden = view !== 'map';
      if (view === 'list') return;

      this.mapLoading.hidden = false;
      this.mapLoading.textContent = 'Karte wird geladen …';
      try {
        await this.loadMapLibrary();
        this.initializeMap();
        this.renderMap();
        this.mapLoading.hidden = true;
        window.requestAnimationFrame(() => {
          this.map.invalidateSize();
          if (options.focusStoreId) this.openMapMarker(options.focusStoreId);
        });
        this.pushMapAnalytics();
      } catch (error) {
        this.mapLoading.hidden = false;
        this.mapLoading.textContent = 'Die Kartenansicht konnte nicht geladen werden. Die Händlerliste bleibt weiterhin verfügbar.';
      }
    }

    loadMapLibrary() {
      if (window.L) return Promise.resolve(window.L);
      if (window.__zslLeafletPromise) return window.__zslLeafletPromise;
      const scriptUrl = this.root.dataset.mapScriptUrl;
      const styleUrl = this.root.dataset.mapStyleUrl;
      if (!scriptUrl || !styleUrl) return Promise.reject(new Error('map-assets'));

      if (!document.querySelector('link[data-zsl-leaflet]')) {
        const stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.href = styleUrl;
        stylesheet.dataset.zslLeaflet = 'true';
        document.head.append(stylesheet);
      }

      window.__zslLeafletPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-zsl-leaflet]');
        if (existing) {
          existing.addEventListener('load', () => resolve(window.L), { once: true });
          existing.addEventListener('error', reject, { once: true });
          return;
        }
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.async = true;
        script.dataset.zslLeaflet = 'true';
        script.addEventListener('load', () => resolve(window.L), { once: true });
        script.addEventListener('error', reject, { once: true });
        document.head.append(script);
      });
      return window.__zslLeafletPromise;
    }

    initializeMap() {
      if (this.map) return;
      this.map = window.L.map(this.mapTarget, {
        scrollWheelZoom: false,
        zoomControl: true
      });
      window.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors'
      }).addTo(this.map);
      this.markerLayer = window.L.layerGroup().addTo(this.map);
    }

    renderMap() {
      if (!this.map || !this.origin) return;
      this.markerLayer.clearLayers();
      this.storeMarkers.clear();
      const partners = this.sortedPartners.slice(0, Math.min(this.visibleLimit, MAP_MAX_STORES));
      const flagshipIncluded = this.flagship && partners.some((store) => store.id === this.flagship.id);
      const mapStores = this.flagship && !flagshipIncluded ? [this.flagship, ...partners] : partners;

      const originMarker = window.L.marker([this.origin.lat, this.origin.lng], {
        icon: this.createMapIcon('origin'),
        zIndexOffset: 900
      }).bindTooltip('Dein Suchstandort', { direction: 'top', offset: [0, -28] });
      this.markerLayer.addLayer(originMarker);

      mapStores.forEach((store) => {
        const marker = window.L.marker([store.lat, store.lng], {
          icon: this.createMapIcon(store.flagship ? 'flagship' : 'partner'),
          zIndexOffset: store.flagship ? 700 : 300
        });
        marker.bindPopup(this.createMapPopup(store));
        marker.on('click', () => this.highlightStore(store.id));
        this.markerLayer.addLayer(marker);
        this.storeMarkers.set(store.id, marker);
      });

      const fitPoints = [[this.origin.lat, this.origin.lng], ...partners.map((store) => [store.lat, store.lng])];
      if (fitPoints.length > 1) {
        this.map.fitBounds(window.L.latLngBounds(fitPoints), { padding: [42, 42], maxZoom: 12 });
      } else {
        this.map.setView([this.origin.lat, this.origin.lng], 11);
      }
    }

    createMapIcon(type) {
      const label = type === 'flagship' ? 'Z' : '';
      return window.L.divIcon({
        className: 'zsl-map-marker-wrap',
        html: `<span class="zsl-map-marker zsl-map-marker--${type}" aria-hidden="true"><span>${label}</span></span>`,
        iconSize: type === 'origin' ? [26, 26] : [36, 44],
        iconAnchor: type === 'origin' ? [13, 13] : [18, 44],
        popupAnchor: type === 'origin' ? [0, -12] : [0, -40]
      });
    }

    createMapPopup(store) {
      const popup = document.createElement('div');
      popup.className = 'zsl-map-popup';
      const title = document.createElement('strong');
      title.textContent = store.name;
      popup.append(title);

      const address = document.createElement('span');
      address.textContent = `${store.street} ${store.number}, ${store.postalCode} ${store.city}`.replace(/\s+,/, ',').trim();
      popup.append(address);

      const distance = store.flagship ? this.flagshipDistance : store.distance;
      if (Number.isFinite(distance)) {
        const distanceText = document.createElement('span');
        distanceText.className = 'zsl-map-popup__distance';
        distanceText.textContent = this.formatDistance(distance);
        popup.append(distanceText);
      }

      const route = document.createElement('a');
      route.target = '_blank';
      route.rel = 'noopener';
      route.textContent = 'Route planen';
      route.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${store.street} ${store.number}, ${store.postalCode} ${store.city}, ${store.country}`)}`;
      popup.append(route);
      return popup;
    }

    openMapMarker(storeId) {
      const marker = this.storeMarkers.get(storeId);
      if (!marker) return;
      marker.openPopup();
      this.map.panTo(marker.getLatLng());
      this.highlightStore(storeId);
    }

    highlightStore(storeId) {
      this.root.querySelectorAll('[data-store-id]').forEach((card) => {
        card.classList.toggle('is-map-active', card.dataset.storeId === storeId);
      });
    }

    pushMapAnalytics() {
      if (!Array.isArray(window.dataLayer)) return;
      window.dataLayer.push({ event: 'storefinder_map_view', result_count: Math.min(this.visibleLimit, MAP_MAX_STORES) });
    }

    createBadge(label, modifier = '') {
      const badge = document.createElement('span');
      badge.className = `zsl__badge ${modifier}`.trim();
      badge.textContent = label;
      return badge;
    }

    formatDistance(distance) {
      const digits = distance < 10 ? 1 : 0;
      return `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(distance)} km`;
    }

    pushAnalytics(method, countryCode) {
      if (!Array.isArray(window.dataLayer)) return;
      window.dataLayer.push({ event: 'storefinder_search', search_method: method, search_country: countryCode || 'device', result_count: this.sortedPartners.length });
    }
  }

  const mount = (scope = document) => {
    scope.querySelectorAll('[data-zsl-root]:not([data-zsl-mounted])').forEach((root) => {
      root.dataset.zslMounted = 'true';
      new StoreLocator(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
